import useTranslation from 'next-translate/useTranslation';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { DatahubEntity, SubtitleAction } from 'src/interfaces/Datahub';
import { ICue } from 'src/interfaces/PlayerData';
import ApplicationService from 'src/services/ApplicationService';
import SubtitleService from 'src/services/SubtitleService';
import {
  SELECTOR_SUBTITLES_ACTIVE_TRACK,
  SELECTOR_SUBTITLES_SUBTITLES,
  SELECTOR_SUBTITLES_SUBTITLE_ENABLED,
} from 'src/store/reducers/subtitles';
import browserUtils from 'src/utils/browserUtils';
import useApplicationData from '../useApplicationData';
import useNotification from '../useNotification';
import useTracking from '../useTracking';
import useUserPreferences from '../useUserPreferences';

const useSubtitle = () => {
  const activeTrack = useSelector(SELECTOR_SUBTITLES_ACTIVE_TRACK);
  const subtitles = useSelector(SELECTOR_SUBTITLES_SUBTITLES);
  const subtitleEnabled = useSelector(SELECTOR_SUBTITLES_SUBTITLE_ENABLED);

  const { t } = useTranslation('common');
  const { showNotification } = useNotification();
  const { trackDatahub } = useTracking();
  const { subtitleLanguage: storedSubtitle } = useUserPreferences();
  const { subtitleLanguage: subtitleParam } = useApplicationData();

  const getDefaultSubtitleLanguage = useCallback(() => {
    const defaultSubtitle = subtitleParam || storedSubtitle || browserUtils.getBrowserLanguage();

    if (SubtitleService.hasSubtitleOfLanguage(defaultSubtitle)) {
      return defaultSubtitle;
    }

    return subtitles?.[0].language || null;
  }, [storedSubtitle, subtitleParam, subtitles]);

  const setSubtitle = useCallback(
    (language: string) => {
      SubtitleService.setActiveSubtitle(language);
      ApplicationService.userPreferences.updatePreferences({
        subtitleLanguage: language,
        subtitleEnabled: Boolean(language),
      });

      trackDatahub(DatahubEntity.SUBTITLE, SubtitleAction.CHANGE);
    },
    [trackDatahub],
  );

  const subtitleLabel = useMemo(() => {
    if (subtitleEnabled && activeTrack) return activeTrack.label;

    return t('toggle.disable');
  }, [subtitleEnabled, activeTrack, t]);

  const notifySubtitleChange = useCallback(
    (_event: Event, enabled: boolean) => {
      const label = SubtitleService.activeTrack?.label || '';
      const status = enabled ? 'On' : 'Off';

      const text = `${t('subtitles')}: ${status} - ${label}`;

      showNotification(text, enabled);
    },
    [showNotification, t],
  );

  const toggleSubtitle = useCallback(() => {
    subtitleEnabled
      ? SubtitleService.disableSubtitle()
      : SubtitleService.setActiveSubtitle(activeTrack?.language || getDefaultSubtitleLanguage());

    ApplicationService.userPreferences.updatePreferences({ subtitleEnabled: !subtitleEnabled });

    trackDatahub(DatahubEntity.SUBTITLE, SubtitleAction.TOGGLE);
  }, [activeTrack, subtitleEnabled, getDefaultSubtitleLanguage, trackDatahub]);

  const addCue = useCallback((newCue: ICue) => {
    SubtitleService.addCue(newCue);
  }, []);

  const removeCue = useCallback((oldCue: ICue) => {
    SubtitleService.removeCue(oldCue);
  }, []);

  return {
    activeTrack,
    setSubtitle,
    subtitles,
    toggleSubtitle,
    notifySubtitleChange,
    getDefaultSubtitleLanguage,
    addCue,
    removeCue,
    subtitleEnabled,
    subtitleLabel,
  };
};

export default useSubtitle;
